import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { onAuthStateChanged, User, signInWithPopup, GoogleAuthProvider, signOut, createUserWithEmailAndPassword, signInWithEmailAndPassword, updateProfile, updatePassword, sendPasswordResetEmail } from 'firebase/auth';
import { collection, query, where, orderBy, onSnapshot, doc, getDoc, deleteDoc, getDocs, writeBatch, getDocFromServer, setDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { auth, db, handleFirestoreError, OperationType } from './firebase';
import { Visual } from './components/Visual';
import { Assessment } from './components/Assessment';
import { Report } from './components/Report';
import { AdminDashboard } from './components/AdminDashboard';
import { MetricsPage } from './components/MetricsPage';
import { StaticPage } from './components/StaticPage';
import { Markdown } from './components/Markdown';
import { UserResult, Category, Feedback, AppConfig, UserProfile, Question } from './types';
import { motion, AnimatePresence } from 'motion/react';
import { ErrorBoundary } from 'react-error-boundary';
import { ChevronDown, ExternalLink, Download, RotateCcw, LogIn, LogOut, History, TrendingUp, TrendingDown, User as UserIcon, Sparkles, Settings, X, CheckCircle2, AlertCircle, Menu, Loader2, Minus, RefreshCw } from 'lucide-react';
import { cn } from './lib/utils';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { useTheme, Theme } from './contexts/ThemeContext';

import logo from './assets/logo.png';

const CAT_LABEL: Partial<Record<Category, string>> = { 
  responsibility: 'Responsibility', 
  selftrust: 'Self Trust', 
  care: 'Care', 
  overall: 'Overall'
};
const CAT_COL: Partial<Record<Category, string>> = { 
  responsibility: 'var(--cat-responsibility)', 
  selftrust: 'var(--cat-selftrust)', 
  care: 'var(--cat-care)', 
  overall: 'var(--cat-overall)'
};

const Dashboard: React.FC = () => {
  const [scores, setScores] = useState({ responsibility: 0.0, selftrust: 0.0, care: 0.0 });
  const [showAssessment, setShowAssessment] = useState(false);
  const [results, setResults] = useState<UserResult[]>([]);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [feedback, setFeedback] = useState<Feedback[]>([]);
  const [expandedCategory, setExpandedCategory] = useState<Category | null>(null);
  const [appConfig, setAppConfig] = useState<AppConfig | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'signup' | 'google' | 'forgot-password'>('signup');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [showDebug, setShowDebug] = useState(false);
  const dbId = (db as any)._databaseId?.database || '(default)';
  const [authError, setAuthError] = useState<string | null>(null);
  const [authSuccess, setAuthSuccess] = useState<string | null>(null);
  const [showProfileSettings, setShowProfileSettings] = useState(false);
  const [newFirstName, setNewFirstName] = useState('');
  const [newLastName, setNewLastName] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [profileUpdateMessage, setProfileUpdateMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const [isDeletingData, setIsDeletingData] = useState(false);
  const [firestoreError, setFirestoreError] = useState<string | null>(null);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [assessmentLimitError, setAssessmentLimitError] = useState<string | null>(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const [selectedResultId, setSelectedResultId] = useState<string | null>(null);
  const [showInsightsLink, setShowInsightsLink] = useState(false);
  const [isAuthLoading, setIsAuthLoading] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (results.length === 0 || showAssessment) {
        setShowInsightsLink(false);
        return;
      }
      const insightsSection = document.getElementById('insights-section');
      if (insightsSection) {
        const rect = insightsSection.getBoundingClientRect();
        // Show if the top of the section is below the viewport
        setShowInsightsLink(rect.top > window.innerHeight - 100);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [results.length, showAssessment]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    let unsubProfile: (() => void) | null = null;
    const unsubscribe = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (unsubProfile) {
        unsubProfile();
        unsubProfile = null;
      }
      if (u) {
        try {
          const userRef = doc(db, 'users', u.uid);
          const userSnap = await getDoc(userRef);
          
          let role: 'admin' | 'user' = 'user';
          const bootstrapEmails = ["johanwikman@gmail.com", "johan@wikmanconsulting.com"];
          
          if (u.email && bootstrapEmails.includes(u.email)) {
            role = 'admin';
          } else if (userSnap.exists()) {
            role = userSnap.data().role as 'admin' | 'user';
          }

          const isPaying = userSnap.exists() ? (userSnap.data().isPaying || false) : false;

          const userData: UserProfile = {
            uid: u.uid,
            email: u.email || '',
            displayName: u.displayName || (userSnap.exists() ? userSnap.data().displayName : null),
            createdAt: userSnap.exists() ? userSnap.data().createdAt : Timestamp.now(),
            lastLogin: Timestamp.now(),
            role: role,
            isPaying: isPaying
          };

          if (import.meta.env.DEV) console.log("Syncing user profile. Role:", role, "Email:", u.email);

          try {
            if (!userSnap.exists()) {
              if (import.meta.env.DEV) console.log("Creating new user document...");
              await setDoc(userRef, userData);
            } else {
              if (import.meta.env.DEV) console.log("Updating existing user document...");
              const updateData: any = {
                lastLogin: userData.lastLogin,
                email: userData.email,
                role: role
              };
              if (u.displayName) {
                updateData.displayName = u.displayName;
              }
              await updateDoc(userRef, updateData);
            }
            if (import.meta.env.DEV) console.log("User profile synced successfully.");
          } catch (err) {
            console.error("Failed to sync user profile to Firestore:", err);
            // We still set the local profile so the UI can function, 
            // but rules might block server-side operations if the write failed.
          }
          
          setUserProfile(userData);

          // Listen real-time to any user profile changes (e.g., admin flagging isPaying or changing role)
          unsubProfile = onSnapshot(userRef, (snap) => {
            if (snap.exists()) {
              const data = snap.data();
              setUserProfile({
                uid: u.uid,
                email: u.email || '',
                displayName: data.displayName || u.displayName || null,
                createdAt: data.createdAt || Timestamp.now(),
                lastLogin: data.lastLogin || Timestamp.now(),
                role: (data.role as 'admin' | 'user') || 'user',
                isPaying: data.isPaying || false
              });
            }
          }, (error) => {
            handleFirestoreError(error, OperationType.GET, `users/${u.uid}`);
          });

        } catch (error) {
          console.error("Error syncing user profile:", error);
        }
      } else {
        setUserProfile(null);
      }
    });
    
    // Validate connection to Firestore
    async function testConnection() {
      try {
        if (import.meta.env.DEV) console.log("Testing Firestore connection to database:", (db as any)._databaseId?.database || '(default)');
        await getDocFromServer(doc(db, 'test', 'connection'));
        if (import.meta.env.DEV) console.log("Firestore connection successful");
      } catch (error) {
        console.error("Firestore connection test failed:", error);
        if(error instanceof Error && error.message.includes('the client is offline')) {
          console.error("Please check your Firebase configuration. The client is offline.");
        }
      }
    }
    testConnection();
    
    if (import.meta.env.DEV) console.log("Current Origin:", window.location.origin);
    
    return () => {
      unsubscribe();
      if (unsubProfile) {
        unsubProfile();
      }
    };
  }, []);

  useEffect(() => {
    if (!user) {
      setResults([]);
      setScores({ responsibility: 0.0, selftrust: 0.0, care: 0.0 });
      return;
    }

    if (import.meta.env.DEV) console.log("Setting up results listener for path:", `users/${user.uid}/results`);
    const q = query(
      collection(db, 'users', user.uid, 'results'),
      orderBy('timestamp', 'desc')
    );

    const unsub = onSnapshot(q, (snap) => {
      if (import.meta.env.DEV) console.log("Results snapshot received. Empty:", snap.empty, "Size:", snap.size);
      const fetchedResults = snap.docs.map(d => ({ ...d.data(), id: d.id } as UserResult));
      if (import.meta.env.DEV) console.log("Fetched results:", fetchedResults.map(r => ({ id: r.id, date: r.timestamp?.toDate().toLocaleString() })));
      setResults(fetchedResults);
      
      if (fetchedResults.length > 0) {
        // If no result is selected, or the selected result is no longer in the list, select the latest
        if (!selectedResultId || !fetchedResults.find(r => r.id === selectedResultId)) {
          setSelectedResultId(fetchedResults[0].id || null);
          setScores(fetchedResults[0].scores);
        } else {
          // Update scores if the selected result's data changed (though results are usually immutable)
          const selected = fetchedResults.find(r => r.id === selectedResultId);
          if (selected) setScores(selected.scores);
        }
      } else {
        setScores({ responsibility: 0.0, selftrust: 0.0, care: 0.0 });
        setSelectedResultId(null);
      }
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, `users/${user.uid}/results`);
    });

    return () => unsub();
  }, [user]);

  const handleSelectResult = (res: UserResult) => {
    setSelectedResultId(res.id || null);
    setScores(res.scores);
    // Scroll to insights section to see details
    document.getElementById('insights-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  const lastResult = results.find(r => r.id === selectedResultId) || results[0] || null;

  const signInWithGoogle = async () => {
    setAuthError(null);
    setIsAuthLoading(true);
    try {
      if (import.meta.env.DEV) console.log("Attempting Google Login. Origin:", window.location.origin);
      await signInWithPopup(auth, new GoogleAuthProvider());
    } catch (err: any) {
      console.error("Login failed:", err);
      setAuthError(getAuthErrorMessage(err.code));
    } finally {
      setIsAuthLoading(false);
    }
  };

  const getAuthErrorMessage = (code: string) => {
    switch (code) {
      case 'auth/user-not-found': return "No account found with this email.";
      case 'auth/wrong-password': return "Incorrect password. Please try again.";
      case 'auth/email-already-in-use': return "An account already exists with this email.";
      case 'auth/invalid-email': return "Please enter a valid email address.";
      case 'auth/weak-password': return "Password should be at least 6 characters.";
      case 'auth/too-many-requests': return "Too many failed attempts. Please try again later.";
      case 'auth/user-disabled': return "This account has been disabled.";
      case 'auth/popup-closed-by-user': return "Sign-in popup was closed before completion.";
      case 'auth/operation-not-allowed': return "Email/Password sign-in is not enabled. Please contact support.";
      case 'auth/network-request-failed': return "Network error. Please check your internet connection.";
      case 'auth/invalid-credential': return "Invalid login credentials. Please check your email and password.";
      default: return "An unexpected error occurred. Please try again.";
    }
  };

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError(null);
    setAuthSuccess(null);
    setIsAuthLoading(true);
    try {
      if (authMode === 'signup') {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const fullName = `${firstName} ${lastName}`.trim();
        await updateProfile(userCredential.user, {
          displayName: fullName
        });
        
        // Ensure Firestore document is updated with the name
        const userRef = doc(db, 'users', userCredential.user.uid);
        const bootstrapEmails = ["johanwikman@gmail.com", "johan@wikmanconsulting.com"];
        const role = (email && bootstrapEmails.includes(email)) ? 'admin' : 'user';
        
        await setDoc(userRef, {
          uid: userCredential.user.uid,
          email: email,
          displayName: fullName,
          createdAt: Timestamp.now(),
          lastLogin: Timestamp.now(),
          role: role
        }, { merge: true });
      } else {
        await signInWithEmailAndPassword(auth, email, password);
      }
    } catch (err: any) {
      setAuthError(getAuthErrorMessage(err.code));
    } finally {
      setIsAuthLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError(null);
    setAuthSuccess(null);
    if (!email) {
      setAuthError("Please enter your email address first.");
      return;
    }
    setIsAuthLoading(true);
    try {
      await sendPasswordResetEmail(auth, email);
      setAuthSuccess("Password reset email sent. Please check your inbox.");
    } catch (err: any) {
      setAuthError(getAuthErrorMessage(err.code));
    } finally {
      setIsAuthLoading(false);
    }
  };

  useEffect(() => {
    if (user && user.displayName) {
      const names = user.displayName.split(' ');
      setNewFirstName(names[0] || '');
      setNewLastName(names.slice(1).join(' ') || '');
    }
  }, [user, showProfileSettings]);

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    
    setIsUpdatingProfile(true);
    setProfileUpdateMessage(null);
    
    try {
      // Update Name
      if (newFirstName || newLastName) {
        await updateProfile(user, {
          displayName: `${newFirstName} ${newLastName}`.trim()
        });
      }
      
      // Update Password
      if (newPassword) {
        if (newPassword !== confirmPassword) {
          throw new Error("Passwords do not match");
        }
        if (newPassword.length < 6) {
          throw new Error("Password must be at least 6 characters");
        }
        await updatePassword(user, newPassword);
      }
      
      setProfileUpdateMessage({ type: 'success', text: 'Profile updated successfully' });
      setNewPassword('');
      setConfirmPassword('');
      
      // Refresh user state
      setUser({ ...user }); 
    } catch (err: any) {
      let message = err.message;
      if (err.code === 'auth/requires-recent-login') {
        message = "For security, please log out and log back in before changing your password.";
      }
      setProfileUpdateMessage({ type: 'error', text: message });
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  const handleDeleteAllData = async () => {
    if (!user) return;
    setIsDeletingData(true);
    try {
      const q = query(collection(db, 'users', user.uid, 'results'));
      const querySnapshot = await getDocs(q);
      const batch = writeBatch(db);
      querySnapshot.forEach((doc) => {
        batch.delete(doc.ref);
      });
      await batch.commit();
      setShowDeleteConfirm(false);
      setShowProfileSettings(false);
      setScores({ responsibility: 0.0, selftrust: 0.0, care: 0.0 });
    } catch (err: any) {
      console.error("Error deleting data:", err);
      try {
        handleFirestoreError(err, OperationType.DELETE, 'results');
      } catch (firestoreErr: any) {
        setProfileUpdateMessage({ type: 'error', text: "Failed to delete data. Missing or insufficient permissions." });
      }
    } finally {
      setIsDeletingData(false);
    }
  };

  const logout = () => {
    signOut(auth);
    setAuthMode('signup');
    setEmail('');
    setPassword('');
    setFirstName('');
    setLastName('');
    setAuthError(null);
    setScores({ responsibility: 0.0, selftrust: 0.0, care: 0.0 });
  };

  const handleTakeAssessment = () => {
    setAssessmentLimitError(null);
    if (!user) {
      setAuthMode('signup');
      document.getElementById('auth-section')?.scrollIntoView({ behavior: 'smooth' });
    } else {
      // Check for daily limit
      const isAdmin = userProfile?.role === 'admin';
      if (!isAdmin && results.length > 0) {
        const lastResult = results[0];
        if (lastResult.timestamp) {
          const lastDate = lastResult.timestamp.toDate();
          const today = new Date();
          if (lastDate.toDateString() === today.toDateString()) {
            setAssessmentLimitError("You have already completed an assessment today. You are limited to one assessment per day to ensure meaningful reflection. Please return tomorrow!");
            return;
          }
        }
      }
      setShowAssessment(true);
    }
  };

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'feedback'), (snap) => {
      setFeedback(snap.docs.map(d => ({ ...d.data(), id: d.id } as Feedback)));
    }, (error) => {
      setFirestoreError(`Feedback Load Error: ${error.message}`);
      handleFirestoreError(error, OperationType.LIST, 'feedback');
    });

    const qUnsub = onSnapshot(query(collection(db, 'questions'), orderBy('order')), (snap) => {
      setQuestions(snap.docs.map(d => ({ ...d.data(), id: d.id } as Question)));
    }, (error) => {
      setFirestoreError(`Questions Load Error: ${error.message}`);
      handleFirestoreError(error, OperationType.LIST, 'questions');
    });

    const configUnsub = onSnapshot(doc(db, 'config', 'app'), (snap) => {
      if (snap.exists()) {
        setAppConfig(snap.data() as AppConfig);
      }
    }, (error) => {
      setFirestoreError(`Config Load Error: ${error.message}`);
      handleFirestoreError(error, OperationType.GET, 'config/app');
    });

    return () => {
      unsub();
      qUnsub();
      configUnsub();
    };
  }, []);

  const handleSliderChange = (cat: Category, val: number) => {
    setScores(prev => ({ ...prev, [cat]: val }));
  };

  const handleDownloadPDF = async () => {
    if (!user || !lastResult) return;
    setIsGeneratingPDF(true);

    const sheetsToRestore: { sheet: CSSStyleSheet; wasDisabled: boolean }[] = [];
    const safeStyles: HTMLStyleElement[] = [];

    try {
      const container = document.getElementById('report-container');
      if (!container) return;

      // Convert tailwind v4 oklch rules on the fly to prevent html2canvas color parsing crashes
      try {
        const originalStyleSheets = Array.from(document.styleSheets) as CSSStyleSheet[];
        for (const sheet of originalStyleSheets) {
          try {
            if (sheet.disabled) continue;
            const rules = Array.from(sheet.cssRules);
            let hasOklch = false;
            let cssText = '';
            for (const rule of rules) {
              const text = rule.cssText;
              if (text.includes('oklch')) {
                hasOklch = true;
              }
              cssText += text + '\n';
            }

            if (hasOklch) {
              // Convert oklch(L C H) or oklch(L C H / A) to equivalent rgb or rgba sRGB colors on the fly
              const safeCss = cssText.replace(/oklch\(([^()]*|\([^()]*\))*\)/gi, (match) => {
                try {
                  const inner = match.substring(6, match.length - 1).trim();
                  const parts = inner.split('/');
                  const colorPartsStr = parts[0].trim();
                  const alphaStr = parts[1] ? parts[1].trim() : null;
                  const colorParts = colorPartsStr.split(/\s+/);
                  
                  if (colorParts.length < 3) {
                    return 'rgb(120, 120, 120)';
                  }
                  
                  const lVal = colorParts[0];
                  const cVal = colorParts[1];
                  const hVal = colorParts[2];
                  
                  let l = 0;
                  if (lVal !== 'none') {
                    l = parseFloat(lVal);
                    if (lVal.includes('%')) {
                      l = l / 100;
                    }
                  }
                  
                  let c = 0;
                  if (cVal !== 'none') {
                    c = parseFloat(cVal);
                  }
                  
                  let h = 0;
                  if (hVal !== 'none') {
                    let hDeg = parseFloat(hVal);
                    if (hVal.includes('rad')) {
                      hDeg = hDeg * (180 / Math.PI);
                    } else if (hVal.includes('grad')) {
                      hDeg = hDeg * 0.9;
                    } else if (hVal.includes('turn')) {
                      hDeg = hDeg * 360;
                    }
                    h = hDeg;
                  }
                  
                  // OKLCH to sRGB conversion
                  const hRad = (h * Math.PI) / 180;
                  const oklab_L = l;
                  const oklab_a = c * Math.cos(hRad);
                  const oklab_b = c * Math.sin(hRad);
                  
                  const l_ = oklab_L + 0.3963377774 * oklab_a + 0.2158037573 * oklab_b;
                  const m_ = oklab_L - 0.1055613458 * oklab_a - 0.0638541728 * oklab_b;
                  const s_ = oklab_L - 0.0894841775 * oklab_a - 1.2914855414 * oklab_b;
                  
                  const l_lin = l_ * l_ * l_;
                  const m_lin = m_ * m_ * m_;
                  const s_lin = s_ * s_ * s_;
                  
                  const r_lin = +4.0767416621 * l_lin - 3.3077115913 * m_lin + 0.2309699292 * s_lin;
                  const g_lin = -1.2684380046 * l_lin + 2.6097574011 * m_lin - 0.3413193965 * s_lin;
                  const b_lin = -0.0041960863 * l_lin - 0.7034186147 * m_lin + 1.7076147010 * s_lin;
                  
                  const transfer = (cLin: number) => {
                    if (cLin <= 0.0031308) {
                      return 12.92 * cLin;
                    }
                    return 1.055 * Math.pow(cLin, 1 / 2.4) - 0.055;
                  };
                  
                  const r = Math.round(Math.max(0, Math.min(1, transfer(r_lin))) * 255);
                  const g = Math.round(Math.max(0, Math.min(1, transfer(g_lin))) * 255);
                  const b = Math.round(Math.max(0, Math.min(1, transfer(b_lin))) * 255);
                  
                  if (alphaStr) {
                    if (alphaStr.includes('var(') || alphaStr.includes('calc(')) {
                      return `rgba(${r}, ${g}, ${b}, 0.95)`;
                    }
                    let alpha = parseFloat(alphaStr);
                    if (alphaStr.includes('%')) {
                      alpha = alpha / 100;
                    }
                    if (isNaN(alpha)) alpha = 1;
                    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
                  }
                  
                  return `rgb(${r}, ${g}, ${b})`;
                } catch (e) {
                  console.warn("Could not parse oklch color:", match, e);
                  return 'rgb(120, 120, 120)';
                }
              });

              const styleEl = document.createElement('style');
              styleEl.textContent = safeCss;
              styleEl.setAttribute('data-html2canvas-safe', 'true');
              document.head.appendChild(styleEl);
              safeStyles.push(styleEl);

              sheetsToRestore.push({ sheet, wasDisabled: sheet.disabled });
              sheet.disabled = true;
            }
          } catch (e) {
            console.warn("Could not preprocess style sheet:", e);
          }
        }
      } catch (styleErr) {
        console.error("Style conversion error:", styleErr);
      }

      // Temporarily show container for capture
      container.style.display = 'block';
      container.style.position = 'fixed';
      container.style.left = '-9999px';
      container.style.top = '0';
      container.style.visibility = 'visible';
      container.style.zIndex = '9999';

      // Give it a moment to ensure all styles and images are ready
      await new Promise(resolve => setTimeout(resolve, 500));

      const headerElement = document.getElementById('pdf-header');
      const sections = Array.from(container.querySelectorAll('.pdf-section')).filter(el => !el.classList.contains('no-pdf'));

      if (!headerElement) throw new Error("Header element not found");

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const margin = 12; // mm
      const footerHeight = 15; // mm
      const contentWidth = pdfWidth - (2 * margin);
      
      // Capture Header
      const headerCanvas = await html2canvas(headerElement, { 
        scale: 2, 
        useCORS: true, 
        backgroundColor: '#ffffff',
        windowWidth: 800
      });
      const headerImgData = headerCanvas.toDataURL('image/jpeg', 0.95);
      const headerImgHeight = (headerCanvas.height * contentWidth) / headerCanvas.width;

      let currentY = margin;

      const addHeaderAndFooter = (pagePdf: jsPDF) => {
        const year = new Date().getFullYear();
        // Header
        pagePdf.addImage(headerImgData, 'JPEG', margin, margin, contentWidth, headerImgHeight);
        
        // Footer
        pagePdf.setFillColor(255, 255, 255);
        pagePdf.rect(0, pdfHeight - footerHeight, pdfWidth, footerHeight, 'F');
        pagePdf.setFontSize(8);
        pagePdf.setTextColor(150, 150, 150);
        pagePdf.text(`© ${year} Sheila Delaney. All rights reserved.`, pdfWidth / 2, pdfHeight - 7, { align: 'center' });
      };

      addHeaderAndFooter(pdf);
      currentY += headerImgHeight + 10;

      for (let i = 0; i < sections.length; i++) {
        const section = sections[i] as HTMLElement;
        const isInsight = section.classList.contains('pdf-insight-section');
        
        const sectionCanvas = await html2canvas(section, { 
          scale: 2, 
          useCORS: true, 
          backgroundColor: '#ffffff',
          windowWidth: 800
        });
        const sectionImgData = sectionCanvas.toDataURL('image/jpeg', 0.95);
        const sectionImgHeight = (sectionCanvas.height * contentWidth) / sectionCanvas.width;

        // Check if we need a new page
        // Force new page for insights, or if content doesn't fit
        const needsNewPage = isInsight || (currentY + sectionImgHeight > pdfHeight - footerHeight - margin);

        if (needsNewPage && i > 0) {
          pdf.addPage();
          addHeaderAndFooter(pdf);
          currentY = margin + headerImgHeight + 10;
        }

        pdf.addImage(sectionImgData, 'JPEG', margin, currentY, contentWidth, sectionImgHeight);
        currentY += sectionImgHeight + 8;
      }

      pdf.save(`Agency-Report-${user.displayName?.replace(/\s+/g, '-') || 'User'}.pdf`);
      
      // Hide container again
      container.style.display = 'none';
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("There was an error generating your PDF. Please try again.");
    } finally {
      // Re-enable and clean up style tags
      for (const { sheet, wasDisabled } of sheetsToRestore) {
        try {
          sheet.disabled = wasDisabled;
        } catch (e) {
          console.warn("Could not restore stylesheet disabled state:", e);
        }
      }
      for (const styleEl of safeStyles) {
        try {
          styleEl.remove();
        } catch (e) {
          console.warn("Could not remove safe style tag:", e);
        }
      }
      setIsGeneratingPDF(false);
    }
  };

  const total = scores.responsibility + scores.selftrust + scores.care;
  const overallScore = total / 3;

  const getFeedback = (cat: Category, score: number) => {
    if (cat === 'overall') {
      return feedback.find(f => {
        if (f.category !== 'overall') return false;
        
        // Check overall score range (sum of 3 categories)
        const matchesOverall = score >= f.minScore && score <= f.maxScore;
        if (!matchesOverall) return false;

        // Check category specific ranges if they are defined
        const matchesResponsibility = (f.responsibilityMin ?? 0) <= scores.responsibility && scores.responsibility <= (f.responsibilityMax ?? 5);
        const matchesSelfTrust = (f.selftrustMin ?? 0) <= scores.selftrust && scores.selftrust <= (f.selftrustMax ?? 5);
        const matchesCare = (f.careMin ?? 0) <= scores.care && scores.care <= (f.careMax ?? 5);

        return matchesResponsibility && matchesSelfTrust && matchesCare;
      });
    }
    return feedback.find(f => f.category === cat && score >= f.minScore && score <= f.maxScore);
  };

  const fbItems = (['overall', 'responsibility', 'selftrust', 'care'] as Category[]).map(cat => {
    let score = 0;
    if (cat === 'overall') {
      score = total;
    } else {
      score = scores[cat as keyof typeof scores];
    }

    return {
      cat,
      score,
      data: getFeedback(cat, score),
      description: appConfig?.categoryDescriptions?.[cat]
    };
  });

  const isAdmin = userProfile?.role === "admin";
  const { theme, setTheme } = useTheme();

  return (
    <div className="min-h-screen bg-bg-main text-text-main flex flex-col items-center px-4 py-8 sm:p-8 md:p-12 transition-colors duration-300">
      {/* DEBUG SECTION - REMOVE LATER */}
      {import.meta.env.DEV && (
        <div className="fixed bottom-4 right-4 z-[9999]">
          <button 
            onClick={() => setShowDebug(!showDebug)}
            className="bg-red-600 text-white text-[0.6rem] px-3 py-1 rounded-full shadow-lg font-bold uppercase tracking-widest"
          >
            {showDebug ? "Close Debug" : "Debug Info"}
          </button>
          {showDebug && (
            <div className="mt-2 p-4 bg-black border border-red-600 rounded-lg text-[0.6rem] font-mono text-left space-y-2 max-w-xs shadow-2xl">
              <p className="text-red-500 font-bold border-b border-red-900 pb-1 mb-2">AUTH & DB DEBUG</p>
              <p><span className="text-gray-500">Origin:</span> {window.location.origin}</p>
              <p><span className="text-gray-500">Hostname:</span> {window.location.hostname}</p>
              <p><span className="text-gray-500">Env Var:</span> {import.meta.env.VITE_FIREBASE_ENV || 'Not Set'}</p>
              <p><span className="text-gray-500">DB ID:</span> {dbId}</p>
              <p><span className="text-gray-500">Project:</span> {auth.app.options.projectId}</p>
              <p><span className="text-gray-500">Auth Domain:</span> {auth.app.options.authDomain}</p>
              <div className="pt-2 border-t border-red-900 space-y-2">
                <p className="text-emerald-400 font-bold underline">Add these to Firebase Console:</p>
                <div className="space-y-1">
                  <code className="bg-gray-900 p-1 rounded block select-all text-emerald-300">{window.location.hostname}</code>
                  <code className="bg-gray-900 p-1 rounded block select-all text-emerald-300">ais-pre-dslo2geuoax6ybekbqb6h6-304220724955.us-west2.run.app</code>
                </div>
                <p className="text-[0.5rem] text-gray-400 italic">Go to: Auth &rarr; Settings &rarr; Authorized Domains</p>
              </div>
            </div>
          )}
        </div>
      )}
      <header className="w-full max-w-5xl flex justify-between items-center mb-12">
        <div className="flex items-center gap-4">
          <img src={logo} alt="Logo" className="w-8 h-8 opacity-80" referrerPolicy="no-referrer" />
          <h1 className="text-[0.75rem] font-light tracking-[0.35em] uppercase text-text-header">THE INSOURCING WAY</h1>
        </div>
        <div className="flex items-center gap-3">
          {user ? (
            <div className="relative" ref={menuRef}>
              <button 
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center gap-3 px-3 md:px-4 py-2 bg-bg-card border border-border-secondary rounded-[5px] hover:border-border-focus transition-all group"
              >
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                <span className="hidden md:block text-[0.6rem] text-text-secondary group-hover:text-text-main tracking-widest uppercase transition-colors">
                  {user.displayName || user.email?.split('@')[0]}
                </span>
                <Menu size={14} className="md:hidden text-text-muted" />
                <ChevronDown size={12} className={cn("hidden md:block text-text-muted transition-transform duration-300", showUserMenu && "rotate-180")} />
              </button>

              <AnimatePresence>
                {showUserMenu && (
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                    className="absolute right-0 mt-2 w-56 bg-bg-main border border-border-main rounded-xl shadow-2xl overflow-hidden z-50 py-1"
                  >
                    <div className="md:hidden px-4 py-3 border-b border-border-main bg-bg-card/50">
                      <p className="text-[0.55rem] text-text-muted tracking-widest uppercase mb-1">Signed in as</p>
                      <p className="text-[0.7rem] text-text-main font-medium truncate">
                        {user.displayName || user.email}
                      </p>
                    </div>
                    <button 
                      onClick={() => {
                        setShowProfileSettings(true);
                        setShowUserMenu(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-[0.65rem] tracking-widest uppercase text-text-secondary hover:text-text-main hover:bg-bg-card transition-all"
                    >
                      <Settings size={14} /> Settings
                    </button>

                    {(results.length > 0 || total > 0) && (
                      <button 
                        onClick={() => {
                          document.getElementById('insights-section')?.scrollIntoView({ behavior: 'smooth' });
                          setShowUserMenu(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 text-[0.65rem] tracking-widest uppercase text-text-secondary hover:text-text-main hover:bg-bg-card transition-all"
                      >
                        <TrendingUp size={14} /> My Results
                      </button>
                    )}
                    
                    {isAdmin && (
                      <>
                        <Link 
                          to="/admin/metrics" 
                          onClick={() => setShowUserMenu(false)}
                          className="w-full flex items-center gap-3 px-4 py-3 text-[0.65rem] tracking-widest uppercase text-text-secondary hover:text-text-main hover:bg-bg-card transition-all"
                        >
                          <TrendingUp size={14} /> Metrics
                        </Link>
                        <Link 
                          to="/admin" 
                          onClick={() => setShowUserMenu(false)}
                          className="w-full flex items-center gap-3 px-4 py-3 text-[0.65rem] tracking-widest uppercase text-text-secondary hover:text-text-main hover:bg-bg-card transition-all"
                        >
                          <Settings size={14} /> Admin
                        </Link>
                      </>
                    )}

                    <div className="h-px bg-border-main mx-2 my-1" />
                    
                    <button 
                      onClick={() => {
                        logout();
                        setShowUserMenu(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-[0.65rem] tracking-widest uppercase text-red-400/70 hover:text-red-400 hover:bg-red-500/5 transition-all"
                    >
                      <LogOut size={14} /> Sign Out
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              {total > 0 && (
                <button 
                  onClick={() => document.getElementById('insights-section')?.scrollIntoView({ behavior: 'smooth' })}
                  className="text-[0.65rem] tracking-widest uppercase text-text-secondary border border-border-secondary bg-bg-card px-4 py-2 rounded-[5px] flex items-center gap-2 hover:bg-bg-main transition-all"
                >
                  <TrendingUp size={12} /> My Results
                </button>
              )}
              <button 
                onClick={() => {
                  setAuthMode('signup');
                  document.getElementById('auth-section')?.scrollIntoView({ behavior: 'smooth' });
                }} 
                className="text-[0.65rem] tracking-widest uppercase text-text-muted border border-border-focus bg-bg-card px-4 py-2 rounded-[5px] flex items-center gap-2 hover:bg-bg-card-hover transition-all"
              >
                <LogIn size={12} /> Sign Up
              </button>
            </div>
          )}
        </div>
      </header>

      <div className="w-full max-w-5xl flex flex-col md:flex-row items-start justify-center gap-12 md:gap-24">
        <div className="flex flex-col items-center gap-8 w-full max-w-[480px]">
          <h2 className="text-[0.8rem] tracking-[0.45em] uppercase text-text-header font-light text-center">AGENCY ASSESSMENT</h2>
          <div className="w-full aspect-square">
            <Visual scores={(user && results.length > 0) ? scores : { responsibility: 5, selftrust: 5, care: 5 }} lightMode={theme !== 'dark'} showLogo={appConfig?.showLogo !== false} />
          </div>
          
          {results.length > 0 && (
            <div className="space-y-1 text-center md:hidden">
              <p className="text-[0.68rem] tracking-[0.18em] uppercase text-text-muted">Agency Score</p>
              <p className="text-4xl font-extralight text-cat-overall tabular-nums">{total.toFixed(1)} <span className="text-sm text-text-muted font-normal">/ 15</span></p>
            </div>
          )}

          {results.length > 0 && (
            <button
              onClick={() => document.getElementById('insights-section')?.scrollIntoView({ behavior: 'smooth' })}
              className="hidden sm:flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#FFE16E] to-[#DBAD38] text-neutral-950 rounded-[5px] font-bold text-[0.65rem] tracking-[0.15em] uppercase hover:scale-105 active:scale-95 transition-transform border border-black/10 mx-auto"
            >
              <Sparkles size={14} />
              View Your Insights
            </button>
          )}

          {results.length === 0 && (
            <button 
              onClick={handleTakeAssessment} 
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#FFE16E] to-[#DBAD38] text-neutral-950 rounded-[5px] font-bold text-[0.65rem] tracking-[0.15em] uppercase hover:scale-105 active:scale-95 transition-transform border border-black/10 mx-auto"
            >
              <Sparkles size={14} />
              Take Assessment
            </button>
          )}

          <AnimatePresence>
            {assessmentLimitError && (
              <motion.div 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                className="mt-6 p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl flex items-start gap-3 max-w-sm mx-auto"
              >
                <AlertCircle className="text-amber-500 shrink-0 mt-0.5" size={18} />
                <div className="space-y-1 text-left">
                  <p className="text-amber-200/90 text-[0.65rem] font-bold uppercase tracking-widest">Daily Limit Reached</p>
                  <p className="text-amber-200/60 text-[0.7rem] leading-relaxed">{assessmentLimitError}</p>
                </div>
                <button onClick={() => setAssessmentLimitError(null)} className="text-amber-500/50 hover:text-amber-500 transition-colors">
                  <X size={16} />
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {user ? (
          <div className="flex flex-col gap-8 w-full max-w-[300px]">
            <div className="space-y-1 hidden md:block">
              <p className="text-[12px] tracking-[0.18em] uppercase text-text-secondary">Agency Score</p>
              <p className="text-4xl font-extralight text-cat-overall tabular-nums">{total.toFixed(1)} <span className="text-sm text-text-secondary font-normal">/ 15</span></p>
              {appConfig?.scoreInsight && (
                <p className="text-[12px] text-text-secondary leading-relaxed mt-2">
                  {appConfig.scoreInsight}
                </p>
              )}
              <p className="text-[0.7rem] text-text-muted leading-relaxed mt-2">
                {appConfig?.categoryShortDescriptions?.['overall'] || appConfig?.categoryDescriptions?.['overall'] || "Your total agency score across all dimensions."}
              </p>
            </div>

            <div className="h-px bg-border-main my-2 hidden md:block" />
  
            {(['responsibility', 'selftrust', 'care'] as Category[]).map(cat => (
              <div key={cat} className="space-y-3">
                <div className="flex justify-between items-baseline">
                  <span className={cn("text-[0.72rem] tracking-[0.18em] uppercase font-normal", cat === 'responsibility' ? 'text-cat-responsibility' : cat === 'selftrust' ? 'text-cat-selftrust' : 'text-cat-care')}>{CAT_LABEL[cat]}</span>
                  <span className={cn("text-xl font-light tabular-nums", cat === 'responsibility' ? 'text-cat-responsibility' : cat === 'selftrust' ? 'text-cat-selftrust' : 'text-cat-care')}>{scores[cat].toFixed(1)}</span>
                </div>
                {isAdmin && (
                  <input
                    type="range"
                    min="0"
                    max="5"
                    step="0.1"
                    value={scores[cat]}
                    onChange={(e) => handleSliderChange(cat, parseFloat(e.target.value))}
                    className="w-full h-0.5 bg-border-secondary rounded-full appearance-none accent-current cursor-pointer"
                    style={{ color: CAT_COL[cat] }}
                  />
                )}
                <p className="text-[12px] text-text-muted leading-relaxed">
                  {appConfig?.categoryShortDescriptions?.[cat] || appConfig?.categoryDescriptions?.[cat] || (
                    cat === 'responsibility' ? 'How much you own your choices and outcomes.' : 
                    cat === 'selftrust' ? 'Confidence in your own judgement and inner compass.' : 
                    'Compassion toward yourself and others as you act.'
                  )}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <div id="auth-section" className="flex flex-col gap-8 w-full max-w-[400px]">
            <div className="space-y-6">
              <div className="bg-bg-card border border-border-secondary rounded-2xl p-8 space-y-6 shadow-xl">
                <div className="prose prose-invert prose-sm">
                  <p 
                    className="text-text-secondary text-[0.9rem] leading-relaxed whitespace-pre-wrap"
                    style={{ color: '#C1C1C1' }}
                  >
                    {appConfig?.aboutText || "This assessment helps you understand and grow your personal agency through three core dimensions: Responsibility, Self-Trust, and Care. By measuring these areas, you can identify where you are strong and where you can focus your growth."}
                  </p>
                </div>
                
                <div className="pt-4 space-y-4">
                  {authMode === 'forgot-password' ? (
                    <form onSubmit={handleForgotPassword} className="space-y-4">
                      <div className="space-y-2">
                        <p className="text-[0.65rem] text-text-muted tracking-widest uppercase text-center pb-2">Reset Password</p>
                        <input 
                          type="email" 
                          placeholder="Email" 
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="w-full bg-bg-input border border-border-main rounded-lg px-4 py-3 text-sm text-text-main outline-none focus:border-border-focus transition-all"
                          required
                        />
                      </div>
                      
                      {authError && (
                        <div className="space-y-2">
                          <p className="text-red-500/80 text-[0.65rem] text-center italic">{authError}</p>
                          {import.meta.env.DEV && (
                            <>
                              <button 
                                onClick={() => setShowDebug(!showDebug)}
                                className="w-full text-[0.55rem] text-text-muted tracking-widest uppercase hover:text-text-secondary transition-colors"
                              >
                                {showDebug ? "[ Hide Debug Info ]" : "[ Show Debug Info ]"}
                              </button>
                              {showDebug && (
                                <div className="mt-2 p-3 bg-black/40 rounded border border-white/5 text-[0.55rem] font-mono text-left space-y-1 opacity-60">
                                  <p className="text-emerald-400 font-bold mb-1 underline">Domains to Authorize in Firebase Console:</p>
                                  <p className="text-emerald-300">1. {window.location.hostname}</p>
                                  <p className="text-emerald-300">2. ais-pre-dslo2geuoax6ybekbqb6h6-304220724955.us-west2.run.app</p>
                                  <div className="h-px bg-white/10 my-2" />
                                  <p>Origin: {window.location.origin}</p>
                                  <p>DB: {dbId}</p>
                                  <p>Project: {auth.app.options.projectId}</p>
                                  <p>Auth Domain: {auth.app.options.authDomain}</p>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      )}
                      {authSuccess && <p className="text-emerald-500/80 text-[0.65rem] text-center italic">{authSuccess}</p>}
                      
                      <button 
                        type="submit" 
                        disabled={isAuthLoading}
                        className="w-full py-4 bg-bg-card border border-border-focus rounded-xl text-text-secondary text-[0.75rem] tracking-[0.25em] uppercase font-bold hover:bg-bg-card-hover hover:border-border-secondary transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
                      >
                        {isAuthLoading ? <Loader2 size={16} className="animate-spin" /> : "Send Reset Email"}
                      </button>
                      
                      <div className="flex flex-col gap-2 pt-2">
                        <button 
                          type="button"
                          onClick={() => {
                            setAuthMode('login');
                            setAuthError(null);
                            setAuthSuccess(null);
                          }}
                          className="text-[0.6rem] tracking-[0.2em] uppercase text-text-muted hover:text-text-secondary transition-all"
                          style={{ color: '#c1c1c1' }}
                        >
                          Back to Sign In
                        </button>
                      </div>
                    </form>
                  ) : (
                    <div className="space-y-6">
                      <form onSubmit={handleEmailAuth} className="space-y-4">
                        <div className="space-y-2">
                          {authMode === 'signup' && (
                            <div className="grid grid-cols-2 gap-2">
                              <input 
                                type="text" 
                                placeholder="First Name" 
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                                className="w-full bg-bg-input border border-border-main rounded-lg px-4 py-3 text-sm text-text-main outline-none focus:border-border-focus transition-all"
                                required
                              />
                              <input 
                                type="text" 
                                placeholder="Last Name" 
                                value={lastName}
                                onChange={(e) => setLastName(e.target.value)}
                                className="w-full bg-bg-input border border-border-main rounded-lg px-4 py-3 text-sm text-text-main outline-none focus:border-border-focus transition-all"
                                required
                              />
                            </div>
                          )}
                          <input 
                            type="email" 
                            placeholder="Email" 
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="w-full bg-bg-input border border-border-main rounded-lg px-4 py-3 text-sm text-text-main outline-none focus:border-border-focus transition-all"
                            required
                          />
                          <input 
                            type="password" 
                            placeholder="Password" 
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="w-full bg-bg-input border border-border-main rounded-lg px-4 py-3 text-sm text-text-main outline-none focus:border-border-focus transition-all"
                            required
                          />
                        </div>
                        
                        {authError && (
                          <div className="space-y-2">
                            <p className="text-red-500/80 text-[0.65rem] text-center italic">{authError}</p>
                            {import.meta.env.DEV && (
                              <>
                                <button 
                                  onClick={() => setShowDebug(!showDebug)}
                                  className="w-full text-[0.55rem] text-text-muted tracking-widest uppercase hover:text-text-secondary transition-colors"
                                >
                                  {showDebug ? "[ Hide Debug Info ]" : "[ Show Debug Info ]"}
                                </button>
                                {showDebug && (
                                  <div className="mt-2 p-3 bg-black/40 rounded border border-white/5 text-[0.55rem] font-mono text-left space-y-1 opacity-60">
                                    <p className="text-emerald-400 font-bold mb-1 underline">Domains to Authorize in Firebase Console:</p>
                                    <p className="text-emerald-300">1. {window.location.hostname}</p>
                                    <p className="text-emerald-300">2. ais-pre-dslo2geuoax6ybekbqb6h6-304220724955.us-west2.run.app</p>
                                    <div className="h-px bg-white/10 my-2" />
                                    <p>Origin: {window.location.origin}</p>
                                    <p>DB: {dbId}</p>
                                    <p>Project: {auth.app.options.projectId}</p>
                                    <p>Auth Domain: {auth.app.options.authDomain}</p>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        )}
                        
                        <button 
                          type="submit" 
                          disabled={isAuthLoading}
                          className="w-full py-4 bg-bg-main border border-border-secondary rounded-xl text-text-secondary text-[0.75rem] tracking-[0.25em] uppercase font-bold hover:bg-bg-input hover:border-border-main transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
                        >
                          {isAuthLoading ? <Loader2 size={16} className="animate-spin" /> : (authMode === 'login' ? 'Sign In' : 'Create Account')}
                        </button>
                        
                        <div className="flex flex-col gap-2 pt-2">
                          {authMode === 'login' && (
                            <button 
                              type="button"
                              onClick={() => {
                                setAuthMode('forgot-password');
                                setAuthError(null);
                                setAuthSuccess(null);
                              }}
                              className="text-[0.6rem] tracking-[0.2em] uppercase text-text-muted hover:text-text-secondary transition-all"
                              style={{ color: '#c1c1c1' }}
                            >
                              Forgot Password?
                            </button>
                          )}
                          <button 
                            type="button"
                            onClick={() => {
                              setAuthMode(authMode === 'login' ? 'signup' : 'login');
                              setAuthError(null);
                              setAuthSuccess(null);
                            }}
                            className="text-[0.6rem] tracking-[0.2em] uppercase text-text-muted hover:text-text-secondary transition-all"
                            style={{ color: '#c1c1c1' }}
                          >
                            {authMode === 'login' ? "Don't have an account? Sign Up" : "Already have an account? Sign In"}
                          </button>
                        </div>
                      </form>

                      <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                          <div className="w-full border-t border-border-main"></div>
                        </div>
                        <div className="relative flex justify-center text-[0.55rem] uppercase tracking-[0.3em]">
                          <span className="bg-bg-card px-4 text-text-muted">OR</span>
                        </div>
                      </div>

                      <button 
                        onClick={signInWithGoogle} 
                        disabled={isAuthLoading}
                        className="w-full py-4 bg-bg-main border border-border-secondary rounded-xl text-text-secondary text-[0.75rem] tracking-[0.25em] uppercase font-bold hover:bg-bg-input hover:border-border-main transition-all shadow-lg flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isAuthLoading ? <Loader2 size={16} className="animate-spin" /> : (
                          <>
                            <LogIn size={16} /> Sign In with Google
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 pt-4">
                {(['responsibility', 'selftrust', 'care'] as Category[]).map(cat => (
                  <div key={cat} className="text-center space-y-1">
                    <div className={cn("w-full h-1 rounded-full opacity-30", cat === 'responsibility' ? 'bg-cat-responsibility' : cat === 'selftrust' ? 'bg-cat-selftrust' : 'bg-cat-care')} />
                    <span className="text-[0.55rem] tracking-widest uppercase text-text-muted">{CAT_LABEL[cat]}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      <AnimatePresence>
        {showProfileSettings && (
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-bg-overlay backdrop-blur-sm"
          >
            <motion.div 
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-bg-card border border-border-secondary rounded-3xl p-8 w-full max-w-md shadow-2xl relative"
            >
              <button 
                onClick={() => {
                  setShowProfileSettings(false);
                  setProfileUpdateMessage(null);
                }}
                className="absolute top-6 right-6 text-text-secondary hover:text-text-main transition-colors"
              >
                <X size={20} />
              </button>
              
              <div className="space-y-6">
                <div className="space-y-1">
                  <h2 className="text-[0.75rem] tracking-[0.3em] uppercase text-text-main font-bold">Profile Settings</h2>
                  <p className="text-[0.6rem] text-text-muted tracking-widest uppercase">Update your information</p>
                </div>

                <div className="space-y-3">
                  <label className="text-[0.55rem] tracking-widest uppercase text-text-muted ml-1">Theme Selection</label>
                  <div className="grid grid-cols-4 gap-2">
                    {(['dark', 'light', 'lightish', 'gray'] as Theme[]).map((t) => (
                      <button
                        key={t}
                        onClick={() => setTheme(t)}
                        className={cn(
                          "flex flex-col items-center gap-2 p-3 rounded-xl border transition-all",
                          theme === t 
                            ? "bg-bg-card border-border-focus text-text-main shadow-lg" 
                            : "bg-bg-input border-border-main text-text-muted hover:border-border-secondary"
                        )}
                      >
                        <div className={cn(
                          "w-full h-8 rounded-md mb-1 border border-border-main",
                          t === 'dark' ? "bg-black" : 
                          t === 'light' ? "bg-white" : 
                          t === 'lightish' ? "bg-[#f5f2ed]" :
                          "bg-[#374151]"
                        )} />
                        <span className="text-[0.6rem] tracking-widest uppercase font-bold">{t}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="h-px bg-border-main my-2" />

                <form onSubmit={handleUpdateProfile} className="space-y-6">
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <label className="text-[0.55rem] tracking-widest uppercase text-text-muted ml-1">First Name</label>
                        <input 
                          type="text" 
                          value={newFirstName}
                          onChange={(e) => setNewFirstName(e.target.value)}
                          className="w-full bg-bg-input border border-border-main rounded-xl px-4 py-3 text-sm text-text-main outline-none focus:border-border-focus transition-all"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-[0.55rem] tracking-widest uppercase text-text-muted ml-1">Last Name</label>
                        <input 
                          type="text" 
                          value={newLastName}
                          onChange={(e) => setNewLastName(e.target.value)}
                          className="w-full bg-bg-input border border-border-main rounded-xl px-4 py-3 text-sm text-text-main outline-none focus:border-border-focus transition-all"
                        />
                      </div>
                    </div>

                    <div className="h-px bg-border-main my-2" />

                    <div className="space-y-1.5">
                      <label className="text-[0.55rem] tracking-widest uppercase text-text-muted ml-1">New Password</label>
                      <input 
                        type="password" 
                        placeholder="Leave blank to keep current"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="w-full bg-bg-input border border-border-main rounded-xl px-4 py-3 text-sm text-text-main outline-none focus:border-border-focus transition-all"
                      />
                    </div>
                    
                    <div className="space-y-1.5">
                      <label className="text-[0.55rem] tracking-widest uppercase text-text-muted ml-1">Confirm New Password</label>
                      <input 
                        type="password" 
                        placeholder="Confirm new password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="w-full bg-bg-input border border-border-main rounded-xl px-4 py-3 text-sm text-text-main outline-none focus:border-border-focus transition-all"
                      />
                    </div>
                  </div>

                  {profileUpdateMessage && (
                    <div className={cn(
                      "flex items-center gap-3 p-3 rounded-xl text-[0.65rem] tracking-widest uppercase",
                      profileUpdateMessage.type === 'success' ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20" : "bg-red-500/10 text-red-400 border border-red-500/20"
                    )}>
                      {profileUpdateMessage.type === 'success' ? <CheckCircle2 size={14} /> : <AlertCircle size={14} />}
                      {profileUpdateMessage.text}
                    </div>
                  )}

                  <button 
                    type="submit" 
                    disabled={isUpdatingProfile}
                    className="w-full py-4 bg-bg-main border border-border-secondary rounded-xl text-text-secondary text-[0.7rem] tracking-[0.25em] uppercase font-bold hover:bg-bg-input hover:border-border-main transition-all shadow-lg disabled:opacity-50"
                  >
                    {isUpdatingProfile ? 'Updating...' : 'Save Changes'}
                  </button>

                  <div className="pt-4 border-t border-border-main space-y-4">
                    <div className="space-y-1">
                      <h3 className="text-[0.6rem] tracking-[0.2em] uppercase text-red-400/80 font-bold">Danger Zone</h3>
                      <p className="text-[0.55rem] text-text-muted tracking-widest uppercase">Irreversible actions</p>
                    </div>
                    <button 
                      type="button"
                      onClick={() => setShowDeleteConfirm(true)}
                      className="w-full py-3 bg-red-500/5 border border-red-500/20 rounded-xl text-red-400/70 text-[0.65rem] tracking-[0.2em] uppercase font-bold hover:bg-red-500/10 hover:border-red-500/40 transition-all"
                    >
                      Delete All Assessment Data
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          </motion.div>
        )}

        {showDeleteConfirm && (
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-bg-overlay backdrop-blur-md"
          >
            <motion.div 
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-bg-card border border-red-500/20 rounded-3xl p-8 w-full max-w-sm shadow-2xl space-y-6 text-center"
            >
              <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto text-red-500">
                <AlertCircle size={32} />
              </div>
              <div className="space-y-2">
                <h2 className="text-[0.75rem] tracking-[0.3em] uppercase text-text-main font-bold">Are you sure?</h2>
                <p className="text-[0.65rem] text-text-muted leading-relaxed">
                  This will permanently delete all your assessment history. This action cannot be undone.
                </p>
              </div>
              <div className="flex flex-col gap-3">
                <button 
                  onClick={handleDeleteAllData}
                  disabled={isDeletingData}
                  className="w-full py-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-[0.7rem] tracking-[0.2em] uppercase font-bold hover:bg-red-500/20 transition-all disabled:opacity-50"
                >
                  {isDeletingData ? 'Deleting...' : 'Yes, Delete Everything'}
                </button>
                <button 
                  onClick={() => setShowDeleteConfirm(false)}
                  className="w-full py-4 bg-transparent text-text-muted text-[0.65rem] tracking-[0.2em] uppercase font-bold hover:text-text-secondary transition-all"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}

        {(results.length > 0 || total > 0) && (
          <motion.div id="insights-section" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-5xl mt-24 pt-12 border-t border-border-main space-y-12">
            <div className="flex justify-between items-center flex-wrap gap-4">
              <div className="space-y-1">
                <span className="text-[16px] tracking-[0.3em] uppercase text-text-header">Your Insights</span>
                <p className="text-[12px] text-text-muted tracking-widest">
                  {lastResult 
                    ? `Based on your assessment from ${lastResult.timestamp?.toDate().toLocaleDateString() || 'today'}`
                    : "Based on your current assessment"
                  }
                </p>
              </div>
              <div className="flex items-center gap-4">
                {user && results.length > 0 && (
                  <button onClick={() => setShowHistory(!showHistory)} className={cn("flex items-center gap-2 text-[12px] tracking-widest transition-colors", showHistory ? "text-text-muted" : "text-text-muted hover:text-text-secondary")}>
                    <History size={14} /> {showHistory ? "Hide History" : "View Progress"}
                  </button>
                )}
                <button onClick={handleTakeAssessment} className="flex items-center gap-2 text-[12px] tracking-widest text-text-muted hover:text-text-secondary transition-colors"><RotateCcw size={14} /> Retake</button>
                {(userProfile?.isPaying === true || userProfile?.role === 'admin') && (
                  <button 
                    onClick={handleDownloadPDF} 
                    disabled={isGeneratingPDF}
                    className="flex items-center gap-2 text-[12px] tracking-widest text-cat-care hover:text-cat-care/80 transition-colors disabled:opacity-50"
                  >
                    {isGeneratingPDF ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                    {isGeneratingPDF ? 'Generating...' : 'Download PDF'}
                  </button>
                )}
              </div>
            </div>

            {showHistory && user && results.length > 0 && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
                <div className="bg-bg-card border border-border-main rounded-2xl p-6 space-y-6">
                  <div className="flex items-center gap-3 mb-4">
                    <TrendingUp size={16} className="text-cat-selftrust" />
                    <h3 className="text-[0.65rem] tracking-[0.2em] uppercase text-text-main">Progress History</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="border-b border-border-secondary">
                          <th className="py-3 text-[0.6rem] tracking-[0.15em] uppercase text-text-muted font-medium">Date</th>
                          <th className="py-3 text-[0.6rem] tracking-[0.15em] uppercase text-cat-responsibility font-medium">Resp.</th>
                          <th className="py-3 text-[0.6rem] tracking-[0.15em] uppercase text-cat-selftrust font-medium">Trust</th>
                          <th className="py-3 text-[0.6rem] tracking-[0.15em] uppercase text-cat-care font-medium">Care</th>
                          <th className="py-3 text-[0.6rem] tracking-[0.15em] uppercase text-cat-overall font-medium">Total</th>
                          <th className="py-3 text-right"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {results.map((res) => {
                          const resTotal = res.scores.responsibility + res.scores.selftrust + res.scores.care;
                          const isSelected = selectedResultId === res.id;
                          return (
                            <tr key={res.id} className={cn("border-b border-border-main hover:bg-bg-card transition-colors group", isSelected && "bg-bg-card border-l-2 border-l-cat-overall")}>
                              <td className="py-4 px-2 text-[0.7rem] text-text-secondary">
                                {res.timestamp.toDate().toLocaleDateString()}
                                {res.id === results[0]?.id && <span className="ml-2 text-[0.5rem] bg-emerald-500/10 text-emerald-500 px-1.5 py-0.5 rounded uppercase tracking-tighter">Latest</span>}
                              </td>
                              <td className="py-4 text-[0.8rem] text-cat-responsibility font-light">{res.scores.responsibility.toFixed(1)}</td>
                              <td className="py-4 text-[0.8rem] text-cat-selftrust font-light">{res.scores.selftrust.toFixed(1)}</td>
                              <td className="py-4 text-[0.8rem] text-cat-care font-light">{res.scores.care.toFixed(1)}</td>
                              <td className="py-4 text-[0.8rem] text-cat-overall font-medium">{resTotal.toFixed(1)}</td>
                              <td className="py-4 text-right pr-2">
                                {isSelected ? (
                                  <span className="text-[0.55rem] tracking-widest uppercase text-cat-overall font-bold">Viewing</span>
                                ) : userProfile?.role === 'admin' ? (
                                  <button onClick={() => handleSelectResult(res)} className="text-[0.6rem] tracking-widest uppercase text-text-muted opacity-0 group-hover:opacity-100 hover:text-text-secondary transition-all">View Details</button>
                                ) : null}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </motion.div>
            )}

            <div key="stats-grid" className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {(['responsibility', 'selftrust', 'care'] as Category[]).map(cat => {
                const score = scores[cat];
                const feedbackData = getFeedback(cat, score);
                return (
                  <div key={cat} className="bg-bg-card border border-border-main rounded-xl p-5 space-y-2">
                    <span className={cn("text-[0.6rem] tracking-[0.18em] uppercase", cat === 'responsibility' ? 'text-cat-responsibility' : cat === 'selftrust' ? 'text-cat-selftrust' : 'text-cat-care')}>{CAT_LABEL[cat]}</span>
                    <div className="space-y-1">
                      <span className={cn("text-3xl font-extralight block", cat === 'responsibility' ? 'text-cat-responsibility' : cat === 'selftrust' ? 'text-cat-selftrust' : 'text-cat-care')}>{score.toFixed(1)} <span className="text-[12px] text-text-muted">/ 5</span></span>
                    </div>
                  </div>
                );
              })}
              <div key="total-agency" className="bg-bg-card border border-border-main rounded-xl p-5 space-y-2">
                <span className="text-[0.6rem] tracking-[0.18em] uppercase text-cat-overall">Agency Total</span>
                <div className="space-y-1">
                  <span className="text-3xl font-extralight block text-cat-overall">{total.toFixed(1)} <span className="text-[12px] text-text-muted">/ 15</span></span>
                </div>
              </div>
            </div>

            {fbItems.length > 0 && (
              <div className="space-y-6">

                {(appConfig.acknowledgmentText || appConfig.howToReadText) && (
                  <div className="space-y-8 pb-8 border-b border-border-main">
                    {appConfig.acknowledgmentText && (
                      <div className="space-y-3">
                        <Markdown className="markdown-body text-text-main text-[16px] leading-relaxed" content={appConfig.acknowledgmentText} />
                      </div>
                    )}
                    {appConfig.howToReadText && (
                      <div className="space-y-3">
                        <h3 className="text-[12px] tracking-[0.25em] uppercase text-text-secondary font-bold">How to Read Your Results</h3>
                        <Markdown className="markdown-body text-text-main text-[16px] leading-relaxed" content={appConfig.howToReadText} />
                      </div>
                    )}
                  </div>
                )}

                <div className="space-y-4">
                  {fbItems.map((item) => {
                    const { cat, score, data } = item;
                    return (
                      <div key={cat} className="bg-bg-main border border-border-main rounded-xl overflow-hidden">
                        <div 
                          className={cn(
                            "p-6 space-y-4 transition-colors group",
                            userProfile?.role === 'admin' && cat !== 'overall' && "cursor-pointer hover:bg-bg-card"
                          )}
                          onClick={() => userProfile?.role === 'admin' && cat !== 'overall' && setExpandedCategory(expandedCategory === cat ? null : cat)}
                        >
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 sm:gap-0">
                            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                              <div className="w-fit">
                                <span className={cn(
                                  "text-[20px] tracking-[0.16em] uppercase font-bold px-3 py-1 rounded-[5px]",
                                  cat === 'responsibility' ? 'bg-cat-responsibility/15 text-cat-responsibility' : 
                                  cat === 'selftrust' ? 'bg-cat-selftrust/15 text-cat-selftrust' : 
                                  cat === 'care' ? 'bg-cat-care/15 text-cat-care' : 
                                  'bg-cat-overall/15 text-cat-overall'
                                )}>{CAT_LABEL[cat]}</span>
                              </div>
                              <div className="flex flex-col gap-1">
                                <div className="flex items-center justify-between sm:justify-start w-full sm:w-auto">
                                  {cat === 'overall' ? (
                                    <span className="text-[12px] text-text-secondary tracking-wider font-normal pt-[5px]">Overall Pattern</span>
                                  ) : (
                                    <span className="text-[12px] text-text-secondary tracking-wider">Score <span className="text-text-main">{score.toFixed(1)}</span> <span className="opacity-40">/ 5.0</span></span>
                                  )}
                                  {userProfile?.role === 'admin' && cat !== 'overall' && (
                                    <div className="flex items-center gap-2 sm:hidden">
                                      <span className="text-[0.55rem] tracking-widest uppercase text-text-muted font-bold opacity-60">
                                        {expandedCategory === cat ? 'Hide Details' : 'View Details'}
                                      </span>
                                      <ChevronDown size={14} className={cn("text-text-muted transition-transform", expandedCategory === cat && "rotate-180")} />
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                            {userProfile?.role === 'admin' && cat !== 'overall' && (
                              <div className="hidden sm:flex items-center gap-2">
                                <span className="text-[0.55rem] tracking-widest uppercase text-text-muted font-bold opacity-60 group-hover:opacity-100 transition-opacity">
                                  {expandedCategory === cat ? 'Hide Details' : 'View Details'}
                                </span>
                                <ChevronDown size={16} className={cn("text-text-muted transition-transform", expandedCategory === cat && "rotate-180")} />
                              </div>
                            )}
                          </div>
                          {item.description && <Markdown className="markdown-body text-text-main text-[16px] leading-relaxed border-l border-border-secondary pl-3" content={item.description} />}
                          <div className="bg-bg-card/30 border border-border-secondary rounded-lg p-4 space-y-3">
                            {data?.title && (
                              <p className={cn(
                                "text-[16px] tracking-[0.2em] uppercase font-bold",
                                cat === 'responsibility' ? 'text-cat-responsibility' : 
                                cat === 'selftrust' ? 'text-cat-selftrust' : 
                                cat === 'care' ? 'text-cat-care' : 
                                'text-cat-overall'
                              )}>{data.title}</p>
                            )}
                            {data?.text ? (
                              <Markdown className="markdown-body text-text-main text-[16px] leading-relaxed" content={data.text} />
                            ) : cat === 'overall' ? (
                              <p className="text-text-main text-[16px] leading-relaxed">
                                Your overall agency is a reflection of how you balance responsibility, self-trust, and care in your daily life.
                              </p>
                            ) : null}
                            <p className={cn(
                              "text-[16px] tracking-[0.2em] uppercase font-bold",
                              cat === 'responsibility' ? 'text-cat-responsibility' : 
                              cat === 'selftrust' ? 'text-cat-selftrust' : 
                              cat === 'care' ? 'text-cat-care' : 
                              'text-cat-overall'
                            )}>Level Up Advice</p>
                            <div key="action-container" className="space-y-3">
                              {data?.levelUpText && (
                                <Markdown key="level-up-text" className="markdown-body text-text-main text-[16px] leading-relaxed" content={data.levelUpText} />
                              )}
                              
                              {cat === 'overall' && !data?.levelUpText && (
                                <p key="overall-default-text" className="text-text-main text-[16px] leading-relaxed">
                                  Focus on consistent practice across all three pillars of agency to improve your overall score.
                                </p>
                              )}

                              {cat !== 'overall' && (() => {
                                const shadowActions = questions
                                  .filter(q => q.category === cat && q.isShadow)
                                  .map(q => {
                                    const answer = lastResult?.answers?.find(a => a.questionId === q.id);
                                    const score = answer?.score || 0;
                                    return { id: q.id, action: q.scoreFeedback?.[score]?.action };
                                  })
                                  .filter(item => !!item.action);

                                if (shadowActions.length === 0 && data?.levelUpText) return null;
                                if (shadowActions.length === 0 && !data?.levelUpText) {
                                  return (
                                    <div key="no-actions" className="space-y-3">
                                      <p className="text-text-main text-[16px] leading-relaxed opacity-50">
                                        No specific actions identified for your current score level.
                                      </p>
                                    </div>
                                  );
                                }

                                return (
                                  <div key="shadow-actions-container" className="space-y-3">
                                    <p className={cn(
                                      "text-[16px] tracking-[0.2em] uppercase font-bold",
                                      cat === 'responsibility' ? 'text-cat-responsibility' : 
                                      cat === 'selftrust' ? 'text-cat-selftrust' : 
                                      cat === 'care' ? 'text-cat-care' : 
                                      'text-cat-overall'
                                    )}>A note on over functioning</p>
                                    {shadowActions.map(item => (
                                      <p key={item.id} className="text-text-main text-[16px] leading-relaxed">
                                        {item.action}
                                      </p>
                                    ))}
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                        {data?.resources && data.resources.length > 0 && (
                          <div className="flex flex-wrap gap-2 pt-2">
                            {data.resources.map((res, i) => (
                              <a key={i} href={res.url} target="_blank" rel="noopener noreferrer" className={cn(
                                "inline-flex items-center gap-2 text-[0.78rem] px-3 py-1.5 rounded-lg border border-border-main transition-all hover:bg-opacity-10",
                                cat === 'responsibility' ? 'text-cat-responsibility hover:bg-cat-responsibility/10 hover:border-cat-responsibility/30' : 
                                cat === 'selftrust' ? 'text-cat-selftrust hover:bg-cat-selftrust/10 hover:border-cat-selftrust/30' : 
                                cat === 'care' ? 'text-cat-care hover:bg-cat-care/10 hover:border-cat-care/30' : 
                                'text-cat-overall hover:bg-cat-overall/10 hover:border-cat-overall/30'
                              )}>
                                <ExternalLink size={12} />
                                {res.label || res.url}
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                      {expandedCategory === cat && cat !== 'overall' && (
                        <div className="bg-bg-card border-t border-border-main p-6 space-y-6">
                          <p className="text-[0.6rem] tracking-[0.2em] uppercase text-text-muted font-bold">Detailed Breakdown</p>
                          <div className="space-y-4">
                            {(() => {
                              const resultIndex = results.findIndex(r => r.id === lastResult?.id);
                              const previousResult = results[resultIndex + 1];
                              
                              return questions.filter(q => q.category === cat).map(q => {
                                const answer = lastResult?.answers?.find(a => a.questionId === q.id);
                                const score = answer?.score || 0;
                                const scoreFeedback = q.scoreFeedback?.[score];

                                const prevAnswer = previousResult?.answers?.find(a => a.questionId === q.id);
                                const prevScore = prevAnswer?.score || 0;
                                const trend = prevScore > 0 ? score - prevScore : 0;

                                return (
                                  <div key={q.id} className="bg-bg-main border border-border-secondary rounded-xl p-5 space-y-4">
                                    <div className="flex justify-between items-center gap-4">
                                      <Markdown className="markdown-body text-[16px] text-text-main leading-relaxed flex-1 min-w-0" content={q.text} />
                                      <div className="flex items-center gap-6 shrink-0">
                                        {previousResult && trend !== 0 && (
                                          <div className="flex items-center gap-1" title={trend > 0 ? 'Improving' : 'Declining'}>
                                            {trend > 0 ? (
                                              <TrendingUp size={16} className="text-emerald-500" />
                                            ) : (
                                              <TrendingDown size={16} className="text-red-500" />
                                            )}
                                          </div>
                                        )}
                                        <div className="flex flex-col items-center gap-1">
                                          <span className="text-[0.6rem] uppercase text-text-muted tracking-widest">Score</span>
                                          <span className={cn(
                                            "w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold",
                                            cat === 'responsibility' ? 'bg-cat-responsibility/20 text-cat-responsibility' : 
                                            cat === 'selftrust' ? 'bg-cat-selftrust/20 text-cat-selftrust' : 
                                            'bg-cat-care/20 text-cat-care'
                                          )}>{score}</span>
                                        </div>
                                      </div>
                                    </div>
                                    
                                    {(scoreFeedback?.feedback || scoreFeedback?.action) ? (
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-border-main">
                                        {scoreFeedback.feedback && (
                                          <div className={cn("space-y-1", !scoreFeedback.action && "md:col-span-2")}>
                                            <p className="text-[12px] tracking-widest uppercase text-text-muted">Feedback</p>
                                            <Markdown className="markdown-body text-[16px] text-text-main leading-relaxed" content={scoreFeedback.feedback} />
                                          </div>
                                        )}
                                          {scoreFeedback.action && (
                                            <div className={cn("space-y-1", !scoreFeedback.feedback && "md:col-span-2")}>
                                              <p className={cn(
                                                "text-[12px] tracking-widest uppercase",
                                                cat === 'responsibility' ? 'text-cat-responsibility' : 
                                                cat === 'selftrust' ? 'text-cat-selftrust' : 
                                                'text-cat-care'
                                              )}>Action</p>
                                              <Markdown className="markdown-body text-[16px] text-text-main leading-relaxed" content={scoreFeedback.action} />
                                            </div>
                                          )}
                                      </div>
                                    ) : (
                                      <p className="text-[0.6rem] text-text-muted italic pt-2 border-t border-border-main">No detailed feedback available for this score.</p>
                                    )}
                                  </div>
                                );
                              });
                            })()}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hidden Report for PDF Generation */}
      <div id="report-container" data-theme="light" style={{ display: 'none' }}>
        <Report 
          user={{ displayName: user?.displayName || 'Guest User', email: user?.email || 'Not signed in' }}
          results={results}
          feedback={feedback}
          questions={questions}
          scores={scores}
          categoryDescriptions={(appConfig?.categoryDescriptions || { 
            responsibility: '', selftrust: '', care: '', overall: '',
            shadow_responsibility: '', shadow_selftrust: '', shadow_care: ''
          }) as Record<Category, string>}
          acknowledgmentText={appConfig?.acknowledgmentText}
          howToReadText={appConfig?.howToReadText}
          scoreInsight={appConfig?.scoreInsight}
          categoryShortDescriptions={(appConfig?.categoryShortDescriptions || { 
            responsibility: '', selftrust: '', care: '', overall: '',
            shadow_responsibility: '', shadow_selftrust: '', shadow_care: ''
          }) as Record<Category, string>}
          showLogo={appConfig?.showLogo !== false}
          pdfExcludeHistory={appConfig?.pdfExcludeHistory}
        />
      </div>

      {showAssessment && (
        <Assessment
          onClose={() => setShowAssessment(false)}
          onComplete={(newScores) => {
            setScores(newScores);
            // Result will be saved in Assessment component and fetched here via onSnapshot
          }}
        />
      )}

      <footer className="w-full max-w-5xl mt-24 pb-12 border-t border-border-main pt-8 flex flex-col items-center gap-6">
        <div className="flex flex-wrap justify-center gap-x-8 gap-y-4">
          <Link to="/page/about" className="text-[0.6rem] tracking-[0.2em] uppercase text-text-muted hover:text-text-main transition-colors">About Us</Link>
          <Link to="/page/privacy" className="text-[0.6rem] tracking-[0.2em] uppercase text-text-muted hover:text-text-main transition-colors">Privacy Policy</Link>
          <Link to="/page/terms" className="text-[0.6rem] tracking-[0.2em] uppercase text-text-muted hover:text-text-main transition-colors">Terms of Service</Link>
        </div>
        <p className="text-[0.65rem] tracking-[0.2em] uppercase text-text-muted font-light flex flex-col sm:flex-row items-center gap-1 sm:gap-2">
          <span>© 2026 Sheila Delaney & Johan Wikman.</span>
          <span>All Rights Reserved.</span>
        </p>
      </footer>

      {/* Mobile Insights Link */}
      <AnimatePresence>
        {showInsightsLink && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[90] sm:hidden w-max"
          >
            <button
              onClick={() => document.getElementById('insights-section')?.scrollIntoView({ behavior: 'smooth' })}
              className="flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-[#FFE16E] to-[#DBAD38] text-neutral-950 rounded-[5px] font-bold text-[0.65rem] tracking-[0.15em] uppercase hover:scale-105 active:scale-95 transition-transform border border-black/10"
            >
              <Sparkles size={14} />
              View Your Insights
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Admin Debug Overlay */}
      {import.meta.env.DEV && userProfile?.role === 'admin' && (
        <div className="fixed bottom-4 right-4 bg-bg-overlay border border-border-main p-4 rounded-xl text-[0.6rem] text-text-muted z-[100] pointer-events-none">
          <p className="uppercase tracking-widest mb-1 text-text-main/40">Admin Debug</p>
          <p>UID: {user?.uid}</p>
          <p>Results: {results.length}</p>
          <p>Selected: {selectedResultId || 'None'}</p>
        </div>
      )}
    </div>
  );
};

function ErrorFallback({ error, resetErrorBoundary }: { error: Error; resetErrorBoundary: () => void }) {
  return (
    <div className="min-h-screen bg-bg-main flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-bg-card border border-border-secondary rounded-2xl p-8 text-center space-y-6 shadow-2xl">
        <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto">
          <AlertCircle className="text-red-500" size={32} />
        </div>
        <div className="space-y-2">
          <h2 className="text-xl font-light text-text-main tracking-tight">Something went wrong</h2>
          <p className="text-text-secondary text-sm leading-relaxed">
            An unexpected error occurred. We've been notified and are looking into it.
          </p>
        </div>
        <div className="bg-bg-main/20 rounded-lg p-4 text-left overflow-auto max-h-32">
          <code className="text-[0.7rem] text-red-400/80 font-mono break-all">
            {error.message}
          </code>
        </div>
        <button
          onClick={resetErrorBoundary}
          className="w-full py-3 bg-cat-overall text-bg-main rounded-xl font-bold text-[0.8rem] tracking-[0.1em] uppercase hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-2"
        >
          <RefreshCw size={16} />
          Try Again
        </button>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={() => {
        window.location.href = '/';
      }}
    >
      <Router>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/metrics" element={<MetricsPage />} />
          <Route path="/page/:pageId" element={<StaticPage />} />
        </Routes>
      </Router>
    </ErrorBoundary>
  );
}
